import React, { useCallback, useState, useEffect } from 'react';
import './musicuploader.css';
import { useDropzone } from 'react-dropzone';
import uploaderDropboxIcon from './images/icons/uploader-dropbox-icon.svg';
import ApiService from './services/api';

function contentTypeForFile(file) {
  if (file.type) return file.type;
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.mp3')) return 'audio/mpeg';
  if (name.endsWith('.zip')) return 'application/zip';
  return 'application/octet-stream';
}

function failedHistoryRow(file, message) {
  const now = new Date();
  return {
    username: '—',
    fileName: file?.name || 'Untitled',
    uploadDate: now.toLocaleDateString(),
    uploadTime: `${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} EST`,
    fileSize: file?.size
      ? `${Math.max(1, Math.round(file.size / (1024 * 1024)))}MB`
      : '—',
    companyName: '',
    status: 'Failed',
    errorMessage: message || 'Upload failed',
  };
}

const emptyMeta = () => ({
  title: '',
  artistName: '',
  album: '',
  recordLabelName: '',
  isrc: '',
});

const MusicUploader = () => {
  const [uploadHistory, setUploadHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState(emptyMeta);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ApiService.listMusicUploads({ includeInitiated: false });
        if (cancelled) return;
        const rows = data?.data?.historyRows || data?.historyRows || [];
        setUploadHistory(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.warn('Failed to load upload history', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadOneFile = useCallback(
    async (file) => {
      const contentType = contentTypeForFile(file);
      const isMp3 =
        contentType.split(';')[0].trim().toLowerCase() === 'audio/mpeg' ||
        (file.name || '').toLowerCase().endsWith('.mp3');
      if (isMp3 && (!meta.title.trim() || !meta.artistName.trim())) {
        throw new Error('Title and Artist are required for commercial MP3 uploads');
      }
      const init = await ApiService.initMusicUpload({
        fileName: file.name,
        contentType,
        fileSizeBytes: file.size,
        title: meta.title.trim() || undefined,
        artistName: meta.artistName.trim() || undefined,
        album: meta.album.trim() || undefined,
        recordLabelName: meta.recordLabelName.trim() || undefined,
        isrc: meta.isrc.trim() || undefined,
      });
      const payload = init?.data || init;
      const uploadUrl = payload.uploadUrl;
      const uploadId = payload.upload?.uploadId;
      const partUploads = payload.partUploads || payload.transfer?.partUploads;
      const requiredHeaders = payload.requiredHeaders || {
        'Content-Type': contentType,
      };

      if (!uploadId) {
        throw new Error('Upload init did not return an upload id');
      }

      if (partUploads?.length) {
        const partSize = payload.transfer.partSizeBytes;
        const partCount = payload.transfer.partCount;
        const etags = [];
        for (const p of partUploads) {
          const start = (p.partNumber - 1) * partSize;
          const end =
            p.partNumber === partCount
              ? file.size
              : Math.min(start + partSize, file.size);
          const slice = file.slice(start, end);
          const put = await fetch(p.uploadUrl, { method: 'PUT', body: slice });
          if (!put.ok) throw new Error(`S3 part upload failed (${put.status})`);
          etags.push({
            partNumber: p.partNumber,
            etag: (put.headers.get('etag') || '').replace(/"/g, ''),
            sizeBytes: end - start,
          });
        }
        if (typeof ApiService.ackMusicUploadParts === 'function') {
          await ApiService.ackMusicUploadParts(uploadId, etags);
        }
      } else if (uploadUrl) {
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: requiredHeaders,
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(`S3 upload failed (${putRes.status})`);
        }
      } else {
        throw new Error('Upload init did not return a upload channel');
      }

      const completed = await ApiService.completeMusicUpload(uploadId);
      const completedPayload = completed?.data || completed;
      return (
        completedPayload.historyRow ||
        failedHistoryRow(file, 'Missing history row after complete')
      );
    },
    [meta],
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      setErrorMessage('');
      setUploading(true);
      try {
        for (const file of acceptedFiles) {
          try {
            const row = await uploadOneFile(file);
            setUploadHistory((prev) => [row, ...prev]);
          } catch (err) {
            const message = err?.message || 'Upload failed';
            setErrorMessage(message);
            setUploadHistory((prev) => [failedHistoryRow(file, message), ...prev]);
          }
        }
      } finally {
        setUploading(false);
      }
    },
    [uploadOneFile],
  );

  const onDropRejected = useCallback((fileRejections) => {
    fileRejections.forEach((rejection) => {
      const file = rejection.file;
      setErrorMessage(
        `File "${file.name}" rejected: Only .mp3 and .zip files are allowed.`,
      );
      setUploadHistory((prev) => [failedHistoryRow(file), ...prev]);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    disabled: uploading,
    accept: {
      'audio/mpeg': ['.mp3'],
      'application/zip': ['.zip'],
    },
    maxSize: 300 * 1024 * 1024,
  });

  const setMetaField = (key) => (e) => {
    setMeta((prev) => ({ ...prev, [key]: e.target.value }));
  };

  return (
    <>
      <h1>Music Uploader</h1>
      <div className="upload-area">
        <div className="provider-meta-form">
          <p className="provider-meta-intro">
            Commercial song details (applied to each file you upload). Title and
            Artist are required for MP3s. Record Label is recommended so the
            track can finish catalog admission automatically.
          </p>
          <div className="provider-meta-grid">
            <label>
              Title <span className="required">*</span>
              <input
                type="text"
                value={meta.title}
                onChange={setMetaField('title')}
                disabled={uploading}
                maxLength={300}
                autoComplete="off"
              />
            </label>
            <label>
              Artist <span className="required">*</span>
              <input
                type="text"
                value={meta.artistName}
                onChange={setMetaField('artistName')}
                disabled={uploading}
                maxLength={300}
                autoComplete="off"
              />
            </label>
            <label>
              Album
              <input
                type="text"
                value={meta.album}
                onChange={setMetaField('album')}
                disabled={uploading}
                maxLength={300}
                autoComplete="off"
              />
            </label>
            <label>
              Record Label
              <input
                type="text"
                value={meta.recordLabelName}
                onChange={setMetaField('recordLabelName')}
                disabled={uploading}
                maxLength={300}
                autoComplete="off"
              />
            </label>
            <label>
              ISRC
              <input
                type="text"
                value={meta.isrc}
                onChange={setMetaField('isrc')}
                disabled={uploading}
                maxLength={32}
                autoComplete="off"
              />
            </label>
          </div>
        </div>
        <div {...getRootProps()} className="upload-box">
          <input {...getInputProps()} />
          <div className="upload-content">
            <h2>CLEAN SONGS ONLY</h2>
            <img
              src={uploaderDropboxIcon}
              alt="Upload"
              className="upload-icon"
            />
            {isDragActive ? (
              <p>Drop the files here...</p>
            ) : (
              <div className="upload-text-container">
                <p>Click or drag and drop to upload folders or files (.zip or .mp3)</p>
                <p className="file-limit">Maximum file size is 300MB.</p>
                {uploading ? <p className="file-limit">Uploading…</p> : null}
              </div>
            )}
          </div>
        </div>
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
        <div className="upload-history">
          <table className="upload-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>File Name</th>
                <th>File Size</th>
                <th>Upload Date</th>
                <th>Upload Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {uploadHistory.map((upload, index) => (
                <tr key={upload.uploadId || index}>
                  <td>{upload.username}</td>
                  <td>{upload.fileName || 'Untitled'}</td>
                  <td>{upload.fileSize}</td>
                  <td>{upload.uploadDate}</td>
                  <td>{upload.uploadTime}</td>
                  <td className={String(upload.status || '').toLowerCase()}>
                    {upload.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <a href="/master-account/musichistory" className="view-history">
            View all history
          </a>
        </div>
      </div>
    </>
  );
};

export default MusicUploader;
